import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/ui/data-table';
import { Button } from '@/components/ui/NewButton';
import { LetterAvatar, Error } from '@/components/shared';
import useMembershipRequests from '@/hooks/useMembershipRequests';
import { backendClient } from '@/lib/api-client';
import type { Matter, MembershipRequest } from '@/types/base';
import { formatDate, getUserTimezone } from '@/utils/date-utils';

type MembershipRequestRow = Omit<MembershipRequest, 'id'> & {
  id: string;
  requestId: number;
};

type ProcessingRequest = {
  requestId: number;
  action: 'approve' | 'reject';
};

const MembershipRequests = ({
  matter,
  onRequestProcessed
}: {
  matter: Matter;
  onRequestProcessed?: () => void;
}) => {
  const { t } = useTranslation('common');
  const [processingRequest, setProcessingRequest] = useState<ProcessingRequest | null>(null);
  const timezone = getUserTimezone();

  const {
    isLoading,
    isError,
    requests,
    mutateMembershipRequests
  } = useMembershipRequests(matter.guid);

  const processRequest = async (requestId: number, action: 'approve' | 'reject') => {
    setProcessingRequest({ requestId, action });
    try {
      await backendClient.post(`/matter/${matter.guid}/process-request/${requestId}`, {
        action,
      });
      onRequestProcessed?.();
      mutateMembershipRequests();

      toast.success(action === 'approve'
        ? t('request-approved')
        : t('request-rejected')
      );

    } catch (error) {
      console.error(`Error ${action}ing request:`, error);
      toast.error(t('error-processing-request'));
    } finally {
      setProcessingRequest(null);
    }
  };

  if (isLoading) {
    return <div />;
  }

  if (isError) {
    return <Error message={isError.message} />;
  }

  if (!requests || requests.length === 0) {
    return (
      <div className="space-y-3 mt-8">
        <div className="space-y-3">
          <h2 className="text-xl font-medium leading-none tracking-tight">
            {t('pending-requests')}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('pending-requests-description')}
          </p>
        </div>
        <div className="p-4 text-center text-gray-500">
          {t('no-pending-requests')}
        </div>
      </div>
    );
  }

  const requestRows: MembershipRequestRow[] = requests.map((request) => ({
    ...request,
    id: request.id.toString(),
    requestId: request.id,
  }));

  const columns: ColumnDef<MembershipRequestRow>[] = [
    {
      accessorKey: 'name',
      header: t('name'),
      cell: ({ row }) => (
        <div className="flex items-center justify-start space-x-2">
          <LetterAvatar name={row.original.name} />
          <span>{row.original.name}</span>
        </div>
      ),
    },
    {
      accessorKey: 'email',
      header: t('email'),
    },
    {
      accessorKey: 'requested_at',
      header: t('requested-at'),
      cell: ({ row }) =>
        formatDate(row.original.requested_at, 'MMM d, h:mm a', timezone),
    },
    {
      id: 'actions',
      header: t('actions'),
      cell: ({ row }) => {
        const isProcessing = processingRequest?.requestId === row.original.requestId;
        const isApproving = isProcessing && processingRequest?.action === 'approve';
        const isRejecting = isProcessing && processingRequest?.action === 'reject';

        return (
          <div className="flex items-center space-x-2">
            <Button
              variant="default"
              size="sm"
              disabled={isProcessing}
              loading={isApproving}
              loadingText={t('approving')}
              onClick={() => processRequest(row.original.requestId, 'approve')}
              text={t('approve')}
            />
            <Button
              variant="outline"
              size="sm"
              disabled={isProcessing}
              loading={isRejecting}
              loadingText={t('rejecting')}
              onClick={() => processRequest(row.original.requestId, 'reject')}
              text={t('reject')}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-3 mt-8">
      <div className="space-y-3">
        <h2 className="text-xl font-medium leading-none tracking-tight">
          {t('pending-requests')}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('pending-requests-description')}
        </p>
      </div>

      <DataTable columns={columns} data={requestRows} hideSearch />
    </div>
  );
};

export default MembershipRequests;